import { ComponentType, FC, Fragment } from 'react';
import { Route, Switch } from 'react-router-dom';

import {
    BreakpointProvider as MagritteBreakpointProvider,
    GridHighlighter,
    SnackbarContextProvider,
} from '@hh.ru/magritte-ui';

import { AppRoutes, PageErrorState } from '../../types';
import ErrorPage from '../components/ErrorPage';
import SPAError from '../components/SPAError';
import StoreDevHelperProvider from '../components/StoreDevHelperProvider';
import DevBuildLoader from '../components/devLoader';
import { TranslationLangContext } from '../components/translation/useTranslations';
import { getServiceName } from '../modules/serviceName';
import { useSelector } from '../modules/useSelector';

interface PageProps {
    proxyComponent?: ComponentType;
    routes: AppRoutes;
    pageWrapper?: ComponentType;
    errorComponent?: ComponentType<PageErrorState>;
    place?: string;
}

const Page: FC<PageProps> = ({ routes, proxyComponent, pageWrapper, errorComponent = ErrorPage }) => {
    const location = useSelector((state) => state.router.location);
    const pageError = useSelector((state) => state.pageError);
    const isProxiedComponent = useSelector(({ isProxiedComponent }) => isProxiedComponent);

    if (pageError) {
        const ErrorComponent = errorComponent;

        return <ErrorComponent code={pageError.code} />;
    }

    if (isProxiedComponent && proxyComponent) {
        const Proxy = proxyComponent;
        return <Proxy />;
    }

    const WrapperComponent = pageWrapper || Fragment;

    return (
        <WrapperComponent>
            <Switch location={location}>
                {routes.map((route) => (
                    <Route exact {...route} key={`${route.path as string}`} />
                ))}
            </Switch>
        </WrapperComponent>
    );
};

const Features = {
    highlightMagritteGrid: 'highlight_magritte_grid',
};

const WrappedPage: FC<PageProps> = (props) => {
    const langs = useSelector(({ langs }) => langs);
    const renderRestriction = useSelector(({ renderRestriction }) => renderRestriction);
    const showGridHighlighter = useSelector(({ features }) => features[Features.highlightMagritteGrid]);
    const serviceName = getServiceName();

    return (
        <StoreDevHelperProvider serviceName={serviceName} place={props.place || 'page'}>
            <DevBuildLoader serviceName={serviceName} />
            <TranslationLangContext.Provider value={langs[0]}>
                <MagritteBreakpointProvider
                    storeActualBreakpointValueInCookie
                    storedBreakpointValue={renderRestriction.magritteBreakpoint}
                    force={renderRestriction.force}
                    renderRestriction={!!renderRestriction}
                >
                    <GridHighlighter show={!!showGridHighlighter} />
                    <SnackbarContextProvider>
                        <SPAError />
                        <Page {...props} />
                    </SnackbarContextProvider>
                </MagritteBreakpointProvider>
            </TranslationLangContext.Provider>
        </StoreDevHelperProvider>
    );
};

export default WrappedPage;
